import {
  AcceptChallengeResultTypenames,
  ChannelType,
  SelectChallengeResultTypenames,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ChallengeType } from 'features/auth/resetPassword/constants/ChallengeType';
import { AddressScreenType } from 'features/auth/verifyAddressChange/constants/addressScreenTypes';
import useChangeAddress from 'features/myVaro/hooks/useChangeAddress';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useAcceptChallengeGql from 'features/otp/hooks/useAcceptChallengeGql';
import useSelectChallengeGql from 'features/otp/hooks/useSelectChallengeGql';
import OtpScreen from 'features/otp/OtpScreen';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { getTrailingDigits } from 'shared/utils/string/getTrailingDigits';

const VERIFY_ADDRESS_SCREEN_ERROR_CODES = [500, 400];

interface SearchParams extends Record<string, string | string[]> {
  challengeId: string;
  phoneNumber: string;
  flowKey: OtpFlowKey;
}
interface VerifyAddressChangeScreenProps {
  otpChallengeType?: ChallengeType.TextMessage | ChallengeType.Email;
}

const VerifyAddressChangeScreen = ({
  otpChallengeType: type,
}: VerifyAddressChangeScreenProps) => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const { t: translatedAddressText } = useTranslation('auth', {
    keyPrefix: 'verifyAddressChange.addressScreenTitle',
  });
  const { challengeId, flowKey, screenType } =
    useLocalSearchParams<SearchParams>();

  const { result } = useGetProfileInfo();
  const customerInfo = result?.customerInfo ?? null;
  const lastFourDigitPhoneNumber = getTrailingDigits(customerInfo?.phone ?? '');
  const customerEmail = customerInfo?.email?.address ?? '';
  const abortControllerRef = useRef<AbortController | null>(null);
  const { acceptChallenge, fetching: isAcceptChallengeLoading } =
    useAcceptChallengeGql();
  const { fetching: resendingOtp, selectChallenge } = useSelectChallengeGql();

  const { loading, handleRiskUpdateAddress } = useChangeAddress({
    channelType: ChannelType.Customer,
    isVloc: false,
    isLimitedAccess: false,
    fromConfirmAddress: false,
    screenType: typeof screenType === 'string' ? screenType : undefined,
  });

  const onBack = () => {
    abortControllerRef.current?.abort();
    router.dismiss();
  };

  useBackHandler(() => {
    onBack();
    return true;
  });

  useEffect(() => {
    const controller = abortControllerRef.current;
    return () => {
      controller?.abort();
    };
  }, []);

  const handleVerifyOtp = async (code: string) => {
    try {
      const result = await acceptChallenge({
        input: {
          challengeId,
          acceptOptions: {
            otpOption: {
              otpCode: code,
            },
          },
        },
      });

      const challengeResult = result.data?.acceptChallenge;

      if (
        challengeResult?.__typename ===
          AcceptChallengeResultTypenames.AcceptChallengeSuccess &&
        challengeResult.challengeVerified
      ) {
        // Navigate immediately to avoid showing success screen
        abortControllerRef.current?.abort();
        await handleRiskUpdateAddress(challengeId);

        return {
          success: true,
          data: {
            challengeId,
          },
        };
      }

      if (
        challengeResult?.__typename ===
        AcceptChallengeResultTypenames.AcceptChallengeError
      ) {
        return {
          errorCode: Number(challengeResult.code),
          message: challengeResult.message,
          title: challengeResult.title,
        };
      }

      // Default fallback if result doesn't match expected shapes
      return {
        errorCode: 500,
        message: translatedTextOtp('genericError'),
        title: translatedTextOtp('verificationFailed'),
      };
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }

      logErrorWithContext(error, {
        featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
        severity: ErrorSeverity.HIGH,
      });
      logBreadcrumb({
        level: 'error',
        message: 'Failed to verify OTP for address change',
      });

      return {
        errorCode: 500,
        message: translatedTextOtp('genericError'),
        title: translatedTextOtp('verificationFailed'),
      };
    }
  };

  const handleResendOtp = async () => {
    try {
      const result = await selectChallenge(challengeId);
      const challengeResult = result.data?.selectChallenge;
      if (
        challengeResult?.__typename ===
        SelectChallengeResultTypenames.SelectChallengeError
      ) {
        return {
          errorCode: Number(challengeResult.code),
          message: challengeResult.message,
          title: challengeResult.title,
        };
      }
      return {};
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }

      logErrorWithContext(error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.HIGH,
      });
      logBreadcrumb({
        level: 'error',
        message: 'Failed to resend OTP for address verification',
      });

      return {};
    }
  };

  const description =
    type === ChallengeType.TextMessage
      ? `${translatedTextOtp('legacy.description.forgotFlowDescription')}${lastFourDigitPhoneNumber}`
      : `${translatedTextOtp('addressRiskChallenge.checkEmail.description')}${customerEmail}.`;

  const headerText =
    type === ChallengeType.TextMessage
      ? translatedTextOtp('legacy.header.forgotFlowTitle')
      : translatedTextOtp('addressRiskChallenge.checkEmail.header');

  const screenTitle =
    (screenType &&
      (screenType === AddressScreenType.HOME
        ? translatedAddressText('home')
        : translatedAddressText('mailing'))) ||
    undefined;

  return (
    <OtpScreen
      flowKey={flowKey}
      verificationType={
        type === ChallengeType.TextMessage
          ? OtpVerificationType.PHONE
          : OtpVerificationType.EMAIL
      }
      description={description}
      headerText={headerText}
      screenTitle={screenTitle}
      isLoading={resendingOtp || isAcceptChallengeLoading || loading}
      screenLevelErrorCodes={VERIFY_ADDRESS_SCREEN_ERROR_CODES}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={onBack}
    />
  );
};

export default VerifyAddressChangeScreen;
